import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import { Loader2, AlertCircle, Plus, Trash2, ChevronsUpDown, Check, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, History, Power, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { FormLifecycleBadge } from '@/components/form-builder/FormLifecycleBadge'
import { Checkbox } from '@/components/ui/checkbox'
import { FormActionChecklist } from '@/components/form-builder/FormActionChecklist'
import { ImportFormMenu } from '@/components/form-builder/ImportFormMenu'
import { ImportFormDialog } from '@/components/form-builder/ImportFormDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  CGT_FORM_LABELS,
  FORM_CATEGORY_LABELS,
  FORM_CATEGORY_COLORS,
  FORM_CATEGORY_OPTIONS,
} from '@/types/cgt'
import type { FormDefinition, FormCategory } from '@/types/cgt'
import { formsApi } from '@/api/formsApi'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { allFields } from '@/lib/formSections'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { useAuth } from '@/providers/AuthProvider'
import { VersionAuditDialog } from '@/components/versioning/VersionAuditDialog'
import { CopyFormDialog } from '@/components/form-builder/CopyFormDialog'
import { formUniqueId, versionLabel } from '@/lib/versioning'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import { useTenantSigners } from '@/hooks/useSigners'
import { PageHeading } from '@/components/layout/PageHeading'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const FORM_CATEGORIES = FORM_CATEGORY_OPTIONS

/** Inline badge style derived from a form type's hex color (tinted bg + border). */
function categoryBadgeStyle(category: FormCategory): CSSProperties {
  const hex = FORM_CATEGORY_COLORS[category] ?? '#6b7280'
  return {
    backgroundColor: `${hex}1a`, // ~10% alpha
    color: hex,
    borderColor: `${hex}40`, // ~25% alpha
  }
}

// Per-user column layout. The expand chevron, Name and the actions cell are
// locked: the chevron is how version history opens, the name identifies the row,
// and the actions are the only route to edit/duplicate/delete. See
// src/components/table/index.ts for the pattern.
const FORM_DEFS_TABLE_ID = 'form-definitions'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
// ids here are the backend's allowed sort keys (FORM_DEFINITION_SORTS in
// form-definitions.service). Unique ID is derived from the family uuid in the
// browser, and Layout and Fields are read out of the fields JSON, so none of the
// three has a column the database can order by.
const FORM_DEFS_COLUMNS: TableColumnDef[] = [
  { id: 'expand', header: 'Version history', locked: true },
  { id: 'name', header: 'Name', locked: true, defaultWidth: 240, sortable: true },
  { id: 'uniqueId', header: 'Unique ID', defaultWidth: 160 },
  { id: 'handle', header: 'Handle', defaultWidth: 160, sortable: true },
  { id: 'formType', header: 'Form Type', defaultWidth: 150, sortable: true },
  { id: 'layout', header: 'Layout', defaultWidth: 120 },
  { id: 'version', header: 'Version', defaultWidth: 100, sortable: true },
  { id: 'status', header: 'Status', defaultWidth: 130, sortable: true },
  { id: 'fields', header: 'Fields', defaultWidth: 100 },
  { id: 'lastUpdate', header: 'Last Version Update', defaultWidth: 180, sortable: true },
  // 5 icon buttons at h-8 (32px) + the labelled 'Edit fields' button + gaps
  // + px-4 padding. Was 260 while the icon buttons still wore `size='sm'`,
  // whose px-3 put 24px of text padding around a 14px icon, five times over.
  { id: 'actions', header: 'Actions', locked: true, defaultWidth: 220 },
]

// The header classes this table already used, kept per column so it renders
// exactly as before.
const FORM_DEF_HEAD_CLASSES: Record<string, string> = {
  expand: 'w-8 px-2 py-3',
  name: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  uniqueId: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  handle: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  formType: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  layout: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  version: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  status: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  fields: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  lastUpdate: 'text-left px-4 py-3 font-medium text-table-header-foreground',
  actions: 'px-4 py-3 text-table-header-foreground',
}

export function FormDefinitionsPage() {
  const cols = useTableColumns(FORM_DEFS_TABLE_ID, FORM_DEFS_COLUMNS)
  // The signer's display name for the audit row. The signature itself is the
  // password the dialog verifies — this is only what the trail shows.
  const { user, email } = useAuth()
  const signerName = user?.fullName ?? email ?? ''

  const navigate = useNavigate()
  // Its own live url, so the form editor's back link returns to this list
  // with its tab and filters intact (z8nrz7czdw).
  const withOrigin = useBackNavLinkBuilder('Forms')
  const { can } = usePermission()
  // Import from a form JSON (z8nrz7ep1z). Runs here rather than in the editor:
  // it creates the form through the ordinary save path and needs no canvas.
  const [importJsonOpen, setImportJsonOpen] = useState(false)
  const [definitions, setDefinitions] = useState<FormDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Set<FormCategory>>(new Set())

  function toggleCategory(cat: FormCategory) {
    setSelectedCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const [anchoredFilter, setAnchoredFilter] = useState<'all' | 'anchored' | 'not-anchored'>('all')
  // Active/Inactive checkboxes — Active pre-checked, Inactive unchecked by default.
  const [showActive, setShowActive] = useState(true)
  const [showInactive, setShowInactive] = useState(false)
  // Expanded family groups (by familyId).
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set())
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  const [total, setTotal] = useState(0)
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)

  // Actions popover state — open for at most one row at a time.
  // Click-away and flipping are Radix's job now; this only remembers which row
  // is open, so at most one popover shows at a time.
  const [actionsPopoverId, setActionsPopoverId] = useState<string | null>(null)

  // Optimistic single-field update against a form definition, reverted on
  // failure. Shared by every "Configure actions" popover toggle so a future
  // one doesn't reimplement the same apply/revert boilerplate again.
  async function applyDefMetaUpdate<K extends 'allowedActions' | 'requiresPatientHistory'>(
    def: FormDefinition,
    key: K,
    next: FormDefinition[K],
  ) {
    const current = def[key]
    setDefinitions(prev => prev.map(d => d.id === def.id ? { ...d, [key]: next } : d))
    try {
      await formsApi.updateDefinitionMeta(def.id, { [key]: next })
    } catch {
      setDefinitions(prev => prev.map(d => d.id === def.id ? { ...d, [key]: current } : d))
    }
  }

  // Multi-field variant of the above. `Repeat` and its count are ONE setting
  // stored in two columns: toggling the action has to clear or seed the count
  // in the same request, or a failed second PATCH leaves a form claiming to
  // repeat with no number (or holding a number nothing repeats).
  async function applyDefMetaPatch(
    def: FormDefinition,
    patch: Pick<FormDefinition, 'allowedActions' | 'defaultRepeatCount'>,
  ) {
    const current = { allowedActions: def.allowedActions, defaultRepeatCount: def.defaultRepeatCount }
    setDefinitions(prev => prev.map(d => d.id === def.id ? { ...d, ...patch } : d))
    try {
      await formsApi.updateDefinitionMeta(def.id, patch)
    } catch {
      setDefinitions(prev => prev.map(d => d.id === def.id ? { ...d, ...current } : d))
    }
  }

  // Defaults true when absent — kits and similar forms are the explicit opt-out.
  async function togglePatientHistory(def: FormDefinition) {
    await applyDefMetaUpdate(def, 'requiresPatientHistory', !(def.requiresPatientHistory ?? true))
  }

  // Signed ACTIVE/INACTIVE toggle (86eybvqgn). Inactivating a version still
  // used by an ACTIVE procedure template is rejected server-side with 409 —
  // the message surfaces inside the signature dialog.
  const [statusTarget, setStatusTarget] = useState<{ def: FormDefinition; toActive: boolean } | null>(null)
  // Sign as roster for the status dialog — loaded only while it is open.
  const statusSigners = useTenantSigners(PERMISSIONS.formsEdit, !!statusTarget)
  const [statusBusy, setStatusBusy] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [copyTarget, setCopyTarget] = useState<FormDefinition | null>(null)

  const [historyFor, setHistoryFor] = useState<FormDefinition | null>(null)
  const loadHistory = useCallback(
    () => (historyFor ? formsApi.getDefinitionVersionAudit(historyFor.id) : Promise.resolve([])),
    [historyFor],
  )

  // The dialog hands back a signing token, not a name: WHO signed is the
  // authenticated account, and only the password proves it is them.
  async function confirmStatusChange(signingToken: string) {
    if (!statusTarget) return
    setStatusBusy(true)
    setStatusError(null)
    try {
      await formsApi.setDefinitionStatus(statusTarget.def.id, {
        active: statusTarget.toActive,
        signingToken,
      })
      setStatusTarget(null)
      setReloadKey(k => k + 1)
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : 'Failed to change the form status.')
    } finally {
      setStatusBusy(false)
    }
  }

  // Delete confirmation dialog. A definition referenced by submissions or a
  // procedure sequence is rejected server-side with 409 — the message is
  // surfaced inside the dialog so the user knows what to remove first.
  const [deleteTarget, setDeleteTarget] = useState<FormDefinition | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [cascadeDelete, setCascadeDelete] = useState(false)

  async function confirmDelete(def: FormDefinition) {
    // Capture before clearing — deleteError holds the procedure name when retrying after a 409.
    const procedureName = deleteError
      ? (deleteError.match(/it is part of procedure "([^"]+)"/) ?? [])[1] ?? null
      : null
    const shouldCascade = cascadeDelete && !!procedureName

    setDeleting(true)
    setDeleteError(null)
    try {
      if (shouldCascade && procedureName) {
        const procedureTemplates = await procedureTemplatesApi.list()
        const proc = procedureTemplates.find(p => p.name === procedureName)
        if (proc) {
          const detail = await procedureTemplatesApi.get(proc.id)
          const relatedDefIds = detail.steps
            .map(s => s.formDefinitionId)
            .filter(id => id !== def.id)
          await procedureTemplatesApi.delete(proc.id)
          const cascadeDeleted: string[] = []
          for (const relId of relatedDefIds) {
            await formsApi.deleteDefinition(relId).then(() => cascadeDeleted.push(relId)).catch(() => {})
          }
          await formsApi.deleteDefinition(def.id)
          setDefinitions(prev => prev.filter(d => d.id !== def.id && !cascadeDeleted.includes(d.id)))
        } else {
          await formsApi.deleteDefinition(def.id)
          setDefinitions(prev => prev.filter(d => d.id !== def.id))
        }
      } else {
        await formsApi.deleteDefinition(def.id)
        setDefinitions(prev => prev.filter(d => d.id !== def.id))
      }
      setDeleteTarget(null)
      setCascadeDelete(false)
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete the form.')
    } finally {
      setDeleting(false)
    }
  }

  // Debounce the search input (300ms) — sends q to the API
  useEffect(() => {
    if (search === debouncedSearch) return
    const id = setTimeout(() => { setDebouncedSearch(search); setSkip(0) }, 300)
    return () => clearTimeout(id)
  }, [search, debouncedSearch])

  useEffect(() => { setSkip(0) }, [selectedCategories, take, showActive, showInactive])

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  // Reset during render so the fetch below never runs for the old page under
  // the new order.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  // Fetch from API whenever server-side params change.
  // Active/Inactive checkboxes drive the `active` query param:
  //   both checked or neither → omit (return all), one checked → filter to that state.
  useEffect(() => {
    setLoading(true)
    setError(null)
    const cats = [...selectedCategories]
    const activeParam =
      showActive === showInactive ? undefined : showActive ? true : false
    formsApi
      .listDefinitions({ q: debouncedSearch, category: cats.length ? cats : undefined, skip, take, active: activeParam, sort })
      .then(({ items, total: t }) => {
        setDefinitions(items)
        setTotal(t)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load definitions.'))
      .finally(() => setLoading(false))
  }, [debouncedSearch, selectedCategories, skip, take, showActive, showInactive, reloadKey, sort])

  // Anchored filter — checks the form category, not individual fields.
  const filtered = definitions.filter(d => {
    if (anchoredFilter === 'all') return true
    if (anchoredFilter === 'anchored') return d.category === 'anchored'
    return d.category !== 'anchored'
  })

  // Group by familyId, sort each group newest → oldest, sort families by max version desc.
  // Under a column sort (z8nrz7e4h6) the server has already ordered the rows,
  // so a family sits where its primary row came back instead.
  const familyGroups: Array<{ familyId: string; primary: FormDefinition; rest: FormDefinition[] }> = (() => {
    const map = new Map<string, FormDefinition[]>()
    for (const d of filtered) {
      const key = d.familyId ?? d.id
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(d)
    }
    const groups = [...map.entries()]
      .map(([familyId, members]) => {
        members.sort((a, b) => (b.version ?? 1) - (a.version ?? 1))
        // Primary = latest active version, fallback to latest overall.
        const primary = members.find(m => m.active !== false) ?? members[0]
        const rest = members.filter(m => m.id !== primary.id)
        return { familyId, primary, rest }
      })
    if (sort) {
      const position = new Map(filtered.map((d, i) => [d.id, i]))
      return groups.sort((a, b) => position.get(a.primary.id)! - position.get(b.primary.id)!)
    }
    return groups.sort((a, b) => (b.primary.version ?? 1) - (a.primary.version ?? 1))
  })()

  return (
    <div className='w-full px-6 py-8'>
        <div className='flex items-center justify-between mb-6'>
          <PageHeading title='Form Definitions' description='Step 1: create blank forms here. Flow is defined later per procedure — not on the form.' />
          <div className='flex items-center gap-2'>
            <ImportFormMenu
              onImportJson={() => setImportJsonOpen(true)}
              // The spreadsheet route hydrates the builder with a draft to
              // review, so it has to land on the editor rather than run here.
              onImportExcel={() => navigate('/form-builder/new?import=excel')}
            />
            <Button onClick={() => navigate('/form-builder/new')}>
              <Plus className='h-4 w-4 mr-2' />
              New Form
            </Button>
          </div>
        </div>

        <ImportFormDialog
          open={importJsonOpen}
          onOpenChange={setImportJsonOpen}
          onCreated={created => navigate(`/form-builder/${created.id}`)}
        />

        <div className='flex flex-wrap items-center gap-3 mb-4'>
          <Input
            placeholder='Search by name, unique ID, or handle…'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className='max-w-sm'
          />
          <div className='relative' ref={categoryDropdownRef}>
            <button
              type='button'
              onClick={() => setCategoryDropdownOpen(v => !v)}
              className='flex h-9 items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring'
            >
              <span className={selectedCategories.size === 0 ? 'text-muted-foreground' : ''}>
                {selectedCategories.size === 0
                  ? 'All form types'
                  : selectedCategories.size === 1
                    ? FORM_CATEGORY_LABELS[[...selectedCategories][0]]
                    : `${selectedCategories.size} types selected`}
              </span>
              <ChevronsUpDown className='h-4 w-4 shrink-0 opacity-50' />
            </button>

            {categoryDropdownOpen && (
              <>
                <div className='fixed inset-0 z-40' onClick={() => setCategoryDropdownOpen(false)} />
                <div className='absolute z-50 mt-1 w-52 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md'>
                  <div className='max-h-64 overflow-y-auto py-1'>
                    {FORM_CATEGORIES.map(cat => {
                      const checked = selectedCategories.has(cat)
                      return (
                        <button
                          key={cat}
                          type='button'
                          onClick={() => toggleCategory(cat)}
                          className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted'
                        >
                          <Check className={`h-4 w-4 shrink-0 ${checked ? 'opacity-100' : 'opacity-0'}`} />
                          <span className='truncate'>{FORM_CATEGORY_LABELS[cat]}</span>
                        </button>
                      )
                    })}
                  </div>
                  {selectedCategories.size > 0 && (
                    <div className='border-t px-3 py-2'>
                      <button
                        type='button'
                        onClick={() => { setSelectedCategories(new Set()); setCategoryDropdownOpen(false) }}
                        className='text-xs text-muted-foreground hover:text-foreground'
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <select
            value={anchoredFilter}
            onChange={e => setAnchoredFilter(e.target.value as 'all' | 'anchored' | 'not-anchored')}
            className='flex h-9 items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring'
          >
            <option value='all'>All forms</option>
            <option value='anchored'>Anchored</option>
            <option value='not-anchored'>Not Anchored</option>
          </select>

          <div className='flex items-center gap-3 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm'>
            <label className='flex cursor-pointer items-center gap-1.5'>
              <Checkbox
                checked={showActive}
                onCheckedChange={v => setShowActive(v === true)}
              />
              Active
            </label>
            <label className='flex cursor-pointer items-center gap-1.5'>
              <Checkbox
                checked={showInactive}
                onCheckedChange={v => setShowInactive(v === true)}
              />
              Inactive
            </label>
          </div>

        </div>

        {loading && (
          <div className='flex items-center justify-center py-20'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {error && (
          <div className='flex items-center gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 mb-4'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error}
          </div>
        )}

        {!loading && familyGroups.length === 0 && (
          <div className='text-center py-16 text-muted-foreground'>
            <p className='text-sm'>
              {search || selectedCategories.size > 0 || !showActive || showInactive
                ? 'No definitions match your filters.'
                : 'No form definitions yet. Create one in the Form Builder.'}
            </p>
          </div>
        )}

        {/* The horizontal scroll below belongs to the TABLE, not the page.
            Without it a wide table overflows into `Layout`'s <main>, which is
            `overflow-auto` — so the entire tab slid sideways, carrying the
            heading and filters with it.

            `overflow-x-persistent`, not `overflow-x-auto`: macOS renders an
            OVERLAY scrollbar that appears only while a scroll is in progress, so
            a table wider than its container reads as one that has simply been
            cut off. The rule in index.css keeps the bar drawn. */}
        {(total > 0 || familyGroups.length > 0) && (
          <div className='overflow-x-persistent rounded-lg border'>
            <table className='w-full text-sm'>
              <thead className='bg-table-header border-b'>
                <tr>
                  {cols.visible.map(({ def }) => (
                    <ResizableTh
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={FORM_DEF_HEAD_CLASSES[def.id]}
                      resizable={def.id !== 'expand' && def.id !== 'actions'}
                    >
                      {def.id === 'expand' ? null : def.header}
                    </ResizableTh>
                  ))}
                </tr>
              </thead>
              <tbody>
                {familyGroups.flatMap(({ familyId, primary, rest }, gi) => {
                  const isExpanded = expandedFamilies.has(familyId)
                  const hasHistory = rest.length > 0
                  const visibleRows: Array<{ def: FormDefinition; isChild: boolean }> = [
                    { def: primary, isChild: false },
                    ...(isExpanded ? rest.map(d => ({ def: d, isChild: true })) : []),
                  ]
                  return visibleRows.map(({ def, isChild }, ri) => {
                    const rowIndex = gi + ri
                    const fields = allFields(def)
                    const hasTable = fields.some(f => f.fieldType === 'table')
                    const hasAnchoredFields = def.category !== 'anchored' && fields.some(
                      f => f.anchored ||
                      (f.tableConfig?.cells != null && Object.values(f.tableConfig.cells).some(c => c.anchored)),
                    )
                    return (
                      <tr
                        key={def.id}
                        className={isChild
                          ? 'bg-muted/30 border-l-2 border-l-border/60'
                          : rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                      >
                        {/* Expand / collapse chevron — only on primary row, only when family has history */}
                        <ColumnCells
                          columns={cols}
                          cells={{
                            // Expand / collapse chevron — only on the primary row, and
                            // only when the family has history.
                            expand: (
                              <td className='px-2 py-3 text-center'>
                                {!isChild && hasHistory && (
                                  <button
                                    type='button'
                                    onClick={() => setExpandedFamilies(prev => {
                                      const next = new Set(prev)
                                      if (next.has(familyId)) next.delete(familyId)
                                      else next.add(familyId)
                                      return next
                                    })}
                                    className='text-muted-foreground hover:text-foreground'
                                    title={isExpanded ? 'Collapse version history' : `Show ${rest.length} older version${rest.length === 1 ? '' : 's'}`}
                                  >
                                    {isExpanded
                                      ? <ChevronDown className='h-4 w-4' />
                                      : <ChevronRightIcon className='h-4 w-4' />}
                                  </button>
                                )}
                              </td>
                            ),
                            name: (
                              <td className={`px-4 py-3 font-medium ${isChild ? 'pl-8' : ''}`}>
                                <div className='flex items-center gap-1.5'>
                                  {hasAnchoredFields && (
                                    <span
                                      className='h-2 w-2 shrink-0 rounded-full bg-red-500'
                                      title='Contains anchored fields'
                                    />
                                  )}
                                  <button
                                    type='button'
                                    className={`text-left hover:underline ${def.active === false ? 'text-muted-foreground' : ''}`}
                                    onClick={() => navigate(withOrigin(`/form-builder/${def.id}`))}
                                  >
                                    {def.name}
                                  </button>
                                </div>
                                {def.latestActiveVersion && (
                                  <span className='mt-1 inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-normal text-amber-800'>
                                    <AlertCircle className='h-3 w-3' />
                                    New form version available:{' '}
                                    {formUniqueId({
                                      id: def.latestActiveVersion.id,
                                      familyId: def.familyId ?? def.id,
                                      version: def.latestActiveVersion.version,
                                    })}
                                  </span>
                                )}
                              </td>
                            ),
                            uniqueId: (
                              <td className='px-4 py-3'>
                                <code className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'>
                                  {formUniqueId(def)}
                                </code>
                              </td>
                            ),
                            handle: (
                              <td className='px-4 py-3'>
                                {def.handle && (
                                  <code
                                    className='rounded bg-muted px-1.5 py-0.5 font-mono text-xs'
                                    title='Permanent handle — used by cross-form references ({handle.fieldId}). Never changes, even when the form is renamed.'
                                  >
                                    {def.handle}
                                  </code>
                                )}
                              </td>
                            ),
                            formType: (
                              <td className='px-4 py-3'>
                                <Badge variant='outline' className='text-xs border' style={categoryBadgeStyle(def.category)}>
                                  {FORM_CATEGORY_LABELS[def.category]}
                                </Badge>
                                {def.formType && (
                                  <span className='ml-2 text-xs text-muted-foreground'>
                                    {CGT_FORM_LABELS[def.formType]}
                                  </span>
                                )}
                              </td>
                            ),
                            layout: (
                              <td className='px-4 py-3'>
                                <Badge
                                  variant='outline'
                                  className={`text-xs ${
                                    hasTable
                                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                                      : 'bg-slate-50 text-slate-600 border-slate-200'
                                  }`}
                                >
                                  {hasTable ? 'Has Table' : 'Standard'}
                                </Badge>
                              </td>
                            ),
                            version: <td className='px-4 py-3 text-muted-foreground'>{def.versionName ?? versionLabel(def.version)}</td>,
                            status: (
                              <td className='px-4 py-3'>
                                <FormLifecycleBadge active={def.active} />
                              </td>
                            ),
                            fields: (
                              <td className='px-4 py-3 text-muted-foreground'>
                                {fields.length} fields
                              </td>
                            ),
                            lastUpdate: (
                              <td className='px-4 py-3 text-muted-foreground text-xs'>
                                {def.lastVersionUpdateAt
                                  ? new Date(def.lastVersionUpdateAt).toLocaleDateString()
                                  : new Date(def.updatedAt).toLocaleDateString()}
                              </td>
                            ),
                            actions: (
                              <td className='px-4 py-3 text-right'>
                                <div className='flex items-center justify-end gap-1'>
                                  {/* Radix, not a hand-positioned absolute panel: the
                                      table now scrolls horizontally inside its own card,
                                      and an absolute child would be clipped by that
                                      scroll container. This portals out of it, and
                                      handles click-away and flipping itself. */}
                                  <Popover
                                    open={actionsPopoverId === def.id}
                                    onOpenChange={open => setActionsPopoverId(open ? def.id : null)}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        size='icon'
                                        variant='ghost'
                                        className='h-8 w-8'
                                        title='Configure actions'
                                      >
                                        <Plus className='h-3.5 w-3.5' />
                                      </Button>
                                    </PopoverTrigger>
                                    {/* `w-64` from dev: it keeps the longest product
                                        operation ("Pool / combine product") on one line
                                        once it is indented under its parent
                                        (z8nrz7dkgg). Its `text-left` is not carried
                                        over — that undid the actions cell's
                                        right-alignment, and a portalled popover is no
                                        longer inside that cell to inherit it. */}
                                    <PopoverContent align='end' className='w-64 p-2'>
                                      <p className='mb-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
                                        Configure actions
                                      </p>
                                      <FormActionChecklist
                                        size='xs'
                                        value={def.allowedActions}
                                        // Both halves in one patch: toggling `Repeat`
                                        // hands back the count to save with it, so the
                                        // action and its number can never be written by
                                        // two requests that could disagree.
                                        onChange={(next, nextRepeatCount) =>
                                          void applyDefMetaPatch(def, {
                                            allowedActions: next,
                                            defaultRepeatCount:
                                              nextRepeatCount !== undefined
                                                ? nextRepeatCount
                                                : def.defaultRepeatCount,
                                          })
                                        }
                                        repeatCount={def.defaultRepeatCount}
                                        onRepeatCountChange={next =>
                                          void applyDefMetaPatch(def, {
                                            allowedActions: def.allowedActions,
                                            defaultRepeatCount: next,
                                          })
                                        }
                                      />
                                      <div className='mt-2 border-t pt-2'>
                                        {/* Same row shape as the checklist above so the
                                            divider reads as a separator, not as a change
                                            of list. */}
                                        <label className='flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 text-xs leading-snug transition-colors hover:bg-muted/60'>
                                          <Checkbox
                                            className='shrink-0'
                                            checked={def.requiresPatientHistory ?? true}
                                            onCheckedChange={() => togglePatientHistory(def)}
                                          />
                                          <span className='flex-1'>Require patient history</span>
                                        </label>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                  <Button
                                    size='sm'
                                    variant='outline'
                                    className='h-8'
                                    onClick={() => navigate(withOrigin(`/form-builder/${def.id}`))}
                                  >
                                    Edit fields
                                  </Button>
                                  {can(PERMISSIONS.formsCreate) && (
                                    <Button
                                      size='icon'
                                      variant='ghost'
                                      className='h-8 w-8'
                                      title='Copy this form into a new form'
                                      onClick={() => setCopyTarget(def)}
                                    >
                                      <Copy className='h-3.5 w-3.5' />
                                    </Button>
                                  )}
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-8 w-8'
                                    title='Version history'
                                    onClick={() => setHistoryFor(def)}
                                  >
                                    <History className='h-3.5 w-3.5' />
                                  </Button>
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-8 w-8'
                                    title={def.active !== false ? 'Inactivate form version' : 'Activate form version'}
                                    onClick={() => {
                                      setStatusError(null)
                                      setStatusTarget({ def, toActive: def.active === false })
                                    }}
                                  >
                                    <Power
                                      className={`h-3.5 w-3.5 ${def.active !== false ? 'text-destructive' : 'text-green-600'}`}
                                    />
                                  </Button>
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-8 w-8'
                                    title='Delete form'
                                    onClick={() => {
                                      setDeleteError(null)
                                      setCascadeDelete(false)
                                      setDeleteTarget(def)
                                    }}
                                  >
                                    <Trash2 className='h-3.5 w-3.5 text-destructive' />
                                  </Button>
                                </div>
                              </td>
                            ),
                          }}
                        />
                      </tr>
                    )
                  })
                })}
              </tbody>
            </table>
            <div className='flex items-center justify-between border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground'>
              <span>
                {total > 0
                  ? `${skip + 1}–${Math.min(skip + take, total)} of ${total} forms`
                  : '0 forms'}
              </span>
              <div className='flex items-center gap-2'>
                <select
                  value={take}
                  onChange={e => { setTake(Number(e.target.value)); setSkip(0) }}
                  className='rounded border bg-background px-2 py-0.5 text-xs'
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <div className='flex items-center gap-1'>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip === 0}
                    onClick={() => setSkip(s => Math.max(0, s - take))}
                  >
                    <ChevronLeft className='h-3.5 w-3.5' />
                    Previous
                  </button>
                  <button
                    className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                    disabled={skip + take >= total}
                    onClick={() => setSkip(s => s + take)}
                  >
                    Next
                    <ChevronRight className='h-3.5 w-3.5' />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Signed ACTIVE/INACTIVE toggle */}
        <SignatureDialog
          open={!!statusTarget}
          onOpenChange={open => !open && !statusBusy && setStatusTarget(null)}
          title={statusTarget?.toActive ? 'Activate Form Version' : 'Inactivate Form Version'}
          description={
            statusTarget
              ? statusTarget.toActive
                ? `”${statusTarget.def.name}” ${statusTarget.def.versionName ?? versionLabel(statusTarget.def.version)} will be offered again to procedure templates.`
                : `”${statusTarget.def.name}” ${statusTarget.def.versionName ?? versionLabel(statusTarget.def.version)} will no longer be offered to procedure templates. A version still used by an ACTIVE template cannot be inactivated.`
              : undefined
          }
          confirmLabel={statusTarget?.toActive ? 'Activate & Sign' : 'Inactivate & Sign'}
          busy={statusBusy}
          error={statusError}
          signerName={signerName}
          signAs={{
            mode: 'optional',
            options: statusSigners.data,
            loading: statusSigners.isLoading,
            error: statusSigners.isError ? 'Could not load the list of signers.' : null,
            selfId: user?.userId ?? null,
          }}
          onConfirm={confirmStatusChange}
        />

        <VersionAuditDialog
          open={!!historyFor}
          onOpenChange={open => !open && setHistoryFor(null)}
          title={`Version history — ${historyFor ? formUniqueId(historyFor) : ''}`}
          load={loadHistory}
        />

        <CopyFormDialog
          open={!!copyTarget}
          source={copyTarget}
          onOpenChange={open => !open && setCopyTarget(null)}
          onCreated={() => setReloadKey(k => k + 1)}
        />

        <Dialog open={!!deleteTarget} onOpenChange={open => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteError(null)
            setCascadeDelete(false)
          }
        }}>
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Delete Form</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This
                cannot be undone. Forms linked to a procedure cannot be deleted until
                that procedure is deleted.
              </DialogDescription>
            </DialogHeader>
            {deleteError && (() => {
              const match = deleteError.match(/it is part of procedure "([^"]+)"/)
              const procedureName = match ? match[1] : null
              return (
                <div className='space-y-2'>
                  <div className='flex items-start gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2'>
                    <AlertCircle className='h-4 w-4 shrink-0 mt-0.5' />
                    {deleteError}
                  </div>
                  {procedureName && (
                    <label className='flex cursor-pointer items-center gap-2 text-sm px-1'>
                      <Checkbox
                        checked={cascadeDelete}
                        onCheckedChange={v => setCascadeDelete(v === true)}
                      />
                      Also delete procedure &ldquo;{procedureName}&rdquo; and all its forms
                    </label>
                  )}
                </div>
              )
            })()}
            <DialogFooter>
              <Button variant='outline' onClick={() => {
                setDeleteTarget(null)
                setDeleteError(null)
                setCascadeDelete(false)
              }} disabled={deleting}>
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => deleteTarget && confirmDelete(deleteTarget)}
                disabled={deleting}
              >
                {deleting && <Loader2 className='h-3.5 w-3.5 animate-spin mr-1.5' />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  )
}
